import type { ReactElement } from 'react';
import { Group, Loader, Select, Text, UnstyledButton } from '@mantine/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { MegaGridPagination } from './MegaGridTypes';

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function MegaGridPaginationFooter({ pagination, loading }: { pagination: MegaGridPagination; loading: boolean }): ReactElement {
    const { page, pageSize, total, totalPages, onPageChange, onPageSizeChange } = pagination;
    const options = pagination.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
    const rangeStart = total > 0 ? (page - 1) * pageSize + 1 : 0;
    const rangeEnd = Math.min(page * pageSize, total);

    return (
        <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--bm-color-border)' }}>
            <Group gap="lg">
                {onPageSizeChange && (
                    <Group gap="xs">
                        <Text size="sm" c="dimmed">
                            Rows per page:
                        </Text>
                        <Select
                            data={options.map((n) => String(n))}
                            value={String(pageSize)}
                            onChange={(val) => val && onPageSizeChange(Number(val))}
                            w={80}
                            size="xs"
                            allowDeselect={false}
                        />
                    </Group>
                )}
                <Group gap="xs" align="center">
                    {loading && <Loader size="xs" />}
                    <Text size="sm" c="dimmed">
                        Showing {rangeStart.toLocaleString('en-US')} to {rangeEnd.toLocaleString('en-US')} of {total.toLocaleString('en-US')}
                    </Text>
                </Group>
            </Group>
            <Group gap="sm" align="center">
                <UnstyledButton
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    style={{ padding: '4px 6px', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--mantine-radius-sm)', opacity: page <= 1 ? 0.3 : 1, display: 'flex', alignItems: 'center' }}
                    aria-label="Previous page"
                >
                    <ChevronLeft size={16} />
                </UnstyledButton>
                <Text size="sm" c="dimmed" lh={1}>
                    Page {page.toLocaleString('en-US')} of {Math.max(1, totalPages).toLocaleString('en-US')}
                </Text>
                <UnstyledButton
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    style={{ padding: '4px 6px', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--mantine-radius-sm)', opacity: page >= totalPages ? 0.3 : 1, display: 'flex', alignItems: 'center' }}
                    aria-label="Next page"
                >
                    <ChevronRight size={16} />
                </UnstyledButton>
            </Group>
        </Group>
    );
}
